<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AutomationHeader from './components/AutomationHeader.vue';
import ExecutionHistory from './components/ExecutionHistory.vue';
import FlowCanvas from './components/FlowCanvas.vue';
import FlowToolbar from './components/FlowToolbar.vue';
import NodeEditor from './components/editors/NodeEditor.vue';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';
import { initializeFlow, exportFlow } from './utils/flowHelpers';

const props = defineProps({
  automationId: {
    type: [String, Number],
    default: null,
  },
  // Aba inicial ao montar o editor. 'history' permite abrir direto no
  // histórico de execuções a partir da listagem (botão "Ver histórico").
  initialTab: {
    type: String,
    default: 'canvas',
    validator: v => ['canvas', 'history'].includes(v),
  },
});

const emit = defineEmits(['close', 'save']);
const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

// Estado local
const isLoading = ref(false);
const isSaving = ref(false);
// Honra a aba inicial pedida pelo pai. 'history' só faz sentido com uma
// automação salva; o guard de render (activeTab === 'history' && automationId)
// e o :disabled do botão garantem coerência se vier sem id.
const activeTab = ref(
  props.initialTab === 'history' && props.automationId ? 'history' : 'canvas'
);
const automationName = ref(t('PIPELINE.FLOW_BUILDER.NEW_AUTOMATION_NAME'));
const automationDescription = ref('');
const isActive = ref(true);

// Run-control state (declared early so the global Esc handler can dismiss
// these overlays). Behaviour lives in the "Run controls" section below.
const isTesting = ref(false);
const isExecuting = ref(false);
const showExecuteConfirm = ref(false);
const showRunModal = ref(false);
const runResult = ref(null);

// ── Dirty-state tracking ───────────────────────────────────────────────────
// Compares a snapshot taken at load (or after a successful save) against the
// current editable state. JSON-serializing the flow makes the comparison
// resilient to Vue proxies and reference equality false positives.
const initialSnapshot = ref(null);

const captureSnapshot = () => {
  const flowData = exportFlow(store);
  return JSON.stringify({
    name: automationName.value,
    description: automationDescription.value,
    active: isActive.value,
    flow: flowData,
  });
};

const isDirty = computed(() => {
  if (isLoading.value) return false;
  if (initialSnapshot.value === null) return false;
  return captureSnapshot() !== initialSnapshot.value;
});

// Sidebar editor state
const editingNodeId = ref(null);
const openNodeEditor = nodeId => {
  editingNodeId.value = nodeId || null;
};

// ── Flow summary (banner se-então) ───────────────────────────────────────
// Resumo natural-language reativo do flow atual. Mostra ao usuário leigo
// o que o flow VAI FAZER em português, sem precisar entender canvas/nodes.
const flowNodes = computed(() => store.state.automationFlow?.flow?.nodes || []);

const summaryTriggerLabel = computed(() => {
  const trigger = flowNodes.value.find(n => n.type === 'trigger');
  if (!trigger) return t('PIPELINE.FLOW_BUILDER.SUMMARY.NO_TRIGGER');
  const subtype = trigger.data?.type || trigger.data?.event_type;
  if (!subtype) return t('PIPELINE.FLOW_BUILDER.SUMMARY.NO_TRIGGER');
  // Lookup label das chaves de toolbar (já existem traduções)
  const key = `PIPELINE.FLOW_BUILDER.NODE.TRIGGER.${subtype}`;
  const translated = t(key);
  return translated !== key ? translated : subtype;
});

const summaryActionCount = computed(
  () => flowNodes.value.filter(n => n.type === 'action').length
);

const summaryActionsLabel = computed(() => {
  if (summaryActionCount.value === 0) {
    return t('PIPELINE.FLOW_BUILDER.SUMMARY.NO_ACTIONS');
  }
  return t(
    'PIPELINE.FLOW_BUILDER.SUMMARY.ACTION_COUNT',
    summaryActionCount.value,
    {
      count: summaryActionCount.value,
    }
  );
});

// Modal para confirmar nome
const showSaveModal = ref(false);
const modalName = ref('');

// Pre-save validation: surface server-side flow validator errors BEFORE
// hitting create/update. Avoids the "saved a broken flow that explodes
// at first execution" pattern. Network errors here are non-fatal — we
// fall through to the save and let server-side validation reject if
// the flow is truly invalid.
const runPreSaveValidation = async flowData => {
  try {
    const { data } = await pipelineAutomationsAPI.validateFlow(flowData);
    return {
      valid: data?.valid !== false,
      errors: data?.errors || [],
      warnings: data?.warnings || [],
    };
  } catch (err) {
    const data = err?.response?.data;
    if (data && data.valid === false) {
      return {
        valid: false,
        errors: data.errors || [],
        warnings: data.warnings || [],
      };
    }
    // Network or unexpected error — degrade gracefully (don't block save).
    return { valid: true, errors: [], warnings: [], networkFailed: true };
  }
};

// Salvar automação
const saveAutomation = async () => {
  isSaving.value = true;
  try {
    const flowData = exportFlow(store);

    // Defesa: bloqueia save com flow visualmente vazio. Evita gravar lixo
    // por engano (clique em Ctrl+S antes do mount completar, p.ex.).
    if (!flowData.nodes || flowData.nodes.length === 0) {
      showAlert(t('PIPELINE.FLOW_BUILDER.SAVE_EMPTY_FLOW_ERROR'));
      return;
    }

    // Pre-save validation — surface errors before the round-trip to create/update.
    const validation = await runPreSaveValidation(flowData);
    if (!validation.valid && validation.errors.length > 0) {
      // Show the first error verbatim. Subsequent errors are still in the
      // payload if we want to render them in a list view later.
      const firstError = validation.errors[0];
      const errorMessage =
        typeof firstError === 'string'
          ? firstError
          : firstError?.message || JSON.stringify(firstError);
      showAlert(
        `${t('PIPELINE.FLOW_BUILDER.VALIDATION_FAILED_PREFIX', 'Validation failed')}: ${errorMessage}`
      );
      return;
    }

    const triggerNode = flowData.nodes.find(n => n.type === 'trigger') || null;
    const triggerType = (() => {
      const sub =
        triggerNode?.data?.type || triggerNode?.data?.event_type || '';
      if (sub === 'scheduled') return 'scheduled';
      // The builder persists the webhook subtype as 'webhook_received'.
      if (sub === 'webhook' || sub === 'webhook_received') return 'webhook';
      if (sub === 'manual') return 'manual';
      return 'event';
    })();

    const automationData = {
      id: props.automationId,
      name: automationName.value,
      description: automationDescription.value,
      active: isActive.value,
      flow: flowData,
      trigger_type: triggerType,
    };

    const saved = await store.dispatch(
      'automationFlow/saveAutomation',
      automationData
    );
    // Keep currentAutomation in sync with the persisted record so node editors
    // can read server-generated fields (e.g. webhook_token for the webhook URL).
    if (saved) {
      store.commit('automationFlow/SET_CURRENT_AUTOMATION', saved);
    }
    showAlert(t('PIPELINE.FLOW_BUILDER.SAVE_SUCCESS'));
    // Reset dirty baseline so the user can immediately close without prompt.
    initialSnapshot.value = captureSnapshot();
    // Propaga o id retornado para que o pai mantenha o editor aberto na
    // automação recém-criada (em vez de fechar e o usuário precisar abrir
    // de novo pela lista).
    emit('save', saved?.id ?? props.automationId);
  } catch {
    showAlert(t('PIPELINE.AUTOMATIONS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Quando clicar em salvar, mostrar modal para confirmar o nome (só para nova automação)
const handleSaveClick = () => {
  if (!props.automationId) {
    // Nova automação - mostrar modal
    modalName.value = automationName.value;
    showSaveModal.value = true;
  } else {
    // Editando automação existente - salvar diretamente
    saveAutomation();
  }
};

// Confirmar salvamento do modal
const confirmSave = () => {
  automationName.value = modalName.value;
  showSaveModal.value = false;
  saveAutomation();
};

// Cancelar modal
const cancelModal = () => {
  showSaveModal.value = false;
};

// Cancelar edição — Modal customizado (substitui window.confirm nativo
// que quebrava i18n e estética). Só prompta se há mudanças não salvas;
// caso contrário fecha direto (era falso positivo abrir o modal toda vez).
const showCancelConfirm = ref(false);
const cancelEditing = () => {
  if (!isDirty.value) {
    emit('close');
    return;
  }
  showCancelConfirm.value = true;
};
const confirmCancel = () => {
  showCancelConfirm.value = false;
  // Reset baseline so the route leave guard and beforeunload don't fire
  // for the very same close we just confirmed.
  initialSnapshot.value = captureSnapshot();
  emit('close');
};
const dismissCancel = () => {
  showCancelConfirm.value = false;
};

// Global keyboard shortcuts: Esc closes the topmost overlay, Ctrl+S saves.
const handleGlobalKeydown = event => {
  if (event.key === 'Escape') {
    // Close in priority order so Esc dismisses one layer at a time.
    if (showRunModal.value) {
      showRunModal.value = false;
      return;
    }
    if (showExecuteConfirm.value) {
      showExecuteConfirm.value = false;
      return;
    }
    const tag = event.target.tagName;
    // Don't yank focus out of a field the user is actively typing in.
    if (
      editingNodeId.value &&
      tag !== 'INPUT' &&
      tag !== 'TEXTAREA' &&
      tag !== 'SELECT'
    ) {
      editingNodeId.value = null;
    }
    return;
  }
  // Ctrl+S — skip when focused inside an input or textarea
  if (!event.ctrlKey || event.key !== 's') return;
  const tag = event.target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return;
  event.preventDefault();
  handleSaveClick();
};

// Browser tab/window close guard — only triggers when there are unsaved
// changes. Browsers ignore the custom message (security), but the prompt
// itself blocks accidental closes.
const handleBeforeUnload = event => {
  if (!isDirty.value) return undefined;
  event.preventDefault();
  // Required for legacy browsers to honor the prompt.
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
  return '';
};

// ── Run controls: dry-run (safe preview) + real execute ───────────────────
// dryRun simula a execução sem efeitos colaterais e devolve os passos; execute
// dispara as ações reais (por isso pede confirmação). O resultado dos dois é
// mostrado no mesmo painel (runResult), distinguindo pelo campo `mode`.
// (state refs declared near the top so the Esc handler can reference them)
const runDryRun = async () => {
  if (!props.automationId || isTesting.value) return;
  isTesting.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.dryRun(
      props.automationId,
      {}
    );
    runResult.value = { mode: 'dry_run', ...data };
    showRunModal.value = true;
  } catch (e) {
    // A simulação devolve 422 quando o flow é inválido — o corpo ainda traz
    // steps/erro, então mostramos o painel em vez de um toast genérico.
    const data = e?.response?.data;
    if (data) {
      runResult.value = { mode: 'dry_run', ...data };
      showRunModal.value = true;
    } else {
      showAlert(t('PIPELINE.FLOW_BUILDER.RUN.DRY_RUN_ERROR'));
    }
  } finally {
    isTesting.value = false;
  }
};

const confirmExecute = () => {
  if (!props.automationId) return;
  showExecuteConfirm.value = true;
};

const runExecute = async () => {
  showExecuteConfirm.value = false;
  if (!props.automationId || isExecuting.value) return;
  isExecuting.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.execute(
      props.automationId,
      {}
    );
    runResult.value = { mode: 'execute', ...data };
    showRunModal.value = true;
    showAlert(
      data?.success === false
        ? t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_FAILED')
        : t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_SUCCESS')
    );
  } catch (e) {
    // Execução real devolve 422 em falha com { success:false, error }. Mostra
    // o detalhe no painel para o usuário saber o PORQUÊ da falha.
    const data = e?.response?.data;
    if (data) {
      runResult.value = { mode: 'execute', ...data };
      showRunModal.value = true;
      showAlert(t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_FAILED'));
    } else {
      showAlert(t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_ERROR'));
    }
  } finally {
    isExecuting.value = false;
  }
};

const closeRunModal = () => {
  showRunModal.value = false;
};

// Normaliza os passos do dry-run para render. O backend devolve
// steps:[{order,node_id,node_type,label,would_execute[],result}].
const runSteps = computed(() => {
  const steps = runResult.value?.steps;
  return Array.isArray(steps) ? steps : [];
});

const runHasError = computed(() => {
  const r = runResult.value;
  if (!r) return false;
  // failed_node_id covers the "completed but a node errored" case where
  // success stays true but a step still failed.
  return (
    r.success === false || !!r.error || !!r.error_message || !!r.failed_node_id
  );
});

const runErrorText = computed(
  () =>
    runResult.value?.error ||
    runResult.value?.error_message ||
    runResult.value?.failed_node_error ||
    ''
);

// Human label for the node that failed, resolved against the loaded flow.
const runFailedNodeLabel = computed(() => {
  const id = runResult.value?.failed_node_id;
  if (!id) return '';
  const node = flowNodes.value.find(n => n.id === id);
  return node?.data?.label || node?.data?.type || id;
});

// Verificações de inicialização
onMounted(async () => {
  document.addEventListener('keydown', handleGlobalKeydown);
  window.addEventListener('beforeunload', handleBeforeUnload);
  try {
    isLoading.value = true;

    if (props.automationId) {
      // Carregamento de automação existente...
      await store.dispatch(
        'automationFlow/fetchAutomation',
        props.automationId
      );
      const automation =
        store.getters['automationFlow/getCurrentAutomation'] ||
        store.getters['automationFlow/getAutomationById'](props.automationId);

      if (automation) {
        automationName.value = automation.name;
        automationDescription.value = automation.description || '';
        isActive.value = !!automation.active;

        // Inicializar o flow com os dados existentes
        initializeFlow(store, automation.flow || {});
      }
    } else {
      // Inicializar uma nova automação
      initializeFlow(store); // Função que configura um novo fluxo com nó de gatilho padrão
    }
  } catch {
    showAlert(t('PIPELINE.AUTOMATIONS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
    // Capture pristine baseline AFTER the load completes. Anything the user
    // does from now on counts as a change.
    initialSnapshot.value = captureSnapshot();
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

// Vue Router guard — fires when navigating away from this route while
// the editor has unsaved changes. The native `confirm` is acceptable
// here because (a) it's a guard, not user-facing copy, (b) it doesn't
// block the screen, and (c) the customized in-editor modal handles
// the close button path which is the common case.
onBeforeRouteLeave((_to, _from, next) => {
  if (!isDirty.value) {
    next();
    return;
  }
  // eslint-disable-next-line no-alert
  const proceed = window.confirm(t('PIPELINE.FLOW_BUILDER.CANCEL_CONFIRM'));
  next(proceed);
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <AutomationHeader
      v-model:name="automationName"
      v-model:active="isActive"
      :is-saving="isSaving"
      :automation-id="props.automationId"
      @save="handleSaveClick"
      @cancel="cancelEditing"
    />

    <!-- Tab navigation -->
    <div
      role="tablist"
      class="flex items-center gap-1 px-4 border-b border-n-weak bg-n-solid-2 flex-shrink-0"
    >
      <button
        role="tab"
        :aria-selected="activeTab === 'canvas' ? 'true' : 'false'"
        class="px-3 py-2.5 -mb-px text-xs font-medium transition-colors border-b-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand rounded-t"
        :class="[
          activeTab === 'canvas'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1',
        ]"
        @click="activeTab = 'canvas'"
      >
        {{ t('PIPELINE.FLOW_BUILDER.TAB_CANVAS') }}
      </button>
      <button
        role="tab"
        :aria-selected="activeTab === 'history' ? 'true' : 'false'"
        class="px-3 py-2.5 -mb-px text-xs font-medium transition-colors border-b-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand rounded-t disabled:opacity-40 disabled:cursor-not-allowed"
        :class="[
          activeTab === 'history'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1',
        ]"
        :disabled="!automationId"
        :title="
          !automationId ? t('PIPELINE.FLOW_BUILDER.TAB_HISTORY_DISABLED') : ''
        "
        @click="activeTab = 'history'"
      >
        {{ t('PIPELINE.FLOW_BUILDER.TAB_HISTORY') }}
      </button>

      <!-- Run controls (right-aligned): dry-run (safe preview) + real execute -->
      <div class="ml-auto flex items-center gap-2 py-1">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-n-slate-11 bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :disabled="!automationId || isTesting"
          :title="
            !automationId ? t('PIPELINE.FLOW_BUILDER.TAB_HISTORY_DISABLED') : ''
          "
          @click="runDryRun"
        >
          <span
            :class="
              isTesting
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-flask-conical'
            "
            class="size-3.5"
            aria-hidden="true"
          />
          {{ t('PIPELINE.FLOW_BUILDER.RUN.TEST') }}
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-n-brand hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1"
          :disabled="!automationId || isExecuting"
          :title="
            !automationId ? t('PIPELINE.FLOW_BUILDER.TAB_HISTORY_DISABLED') : ''
          "
          @click="confirmExecute"
        >
          <span
            :class="
              isExecuting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-play'
            "
            class="size-3.5"
            aria-hidden="true"
          />
          {{ t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE') }}
        </button>
      </div>
    </div>

    <!-- Banner se-então: resumo natural-language do que o flow VAI FAZER -->
    <div
      v-if="activeTab === 'canvas'"
      class="px-4 py-2 bg-n-alpha-1/40 border-b border-n-weak flex items-center gap-3 text-xs flex-shrink-0"
      role="region"
      :aria-label="t('PIPELINE.FLOW_BUILDER.SUMMARY.BANNER_LABEL')"
    >
      <span
        class="i-lucide-zap size-3.5 text-n-amber-11 shrink-0"
        aria-hidden="true"
      />
      <span class="text-n-slate-10 font-semibold uppercase tracking-wider">
        {{ t('PIPELINE.FLOW_BUILDER.SUMMARY.WHEN') }}
      </span>
      <span class="text-n-slate-12 font-medium truncate">
        {{ summaryTriggerLabel }}
      </span>
      <span
        class="i-lucide-arrow-right size-3 text-n-slate-9 shrink-0"
        aria-hidden="true"
      />
      <span class="text-n-slate-10 font-semibold uppercase tracking-wider">
        {{ t('PIPELINE.FLOW_BUILDER.SUMMARY.THEN') }}
      </span>
      <span class="text-n-slate-12 font-medium truncate">
        {{ summaryActionsLabel }}
      </span>
    </div>

    <div class="flex flex-row flex-grow overflow-hidden">
      <template v-if="activeTab === 'canvas'">
        <div class="flex-1 flex overflow-hidden">
          <FlowCanvas :is-loading="isLoading" @edit-node="openNodeEditor" />
          <FlowToolbar />
        </div>

        <!-- Node Editor Sidebar -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 translate-x-4"
          enter-to-class="opacity-100 translate-x-0"
          leave-from-class="opacity-100 translate-x-0"
          leave-to-class="opacity-0 translate-x-4"
        >
          <div
            v-if="editingNodeId"
            role="region"
            :aria-label="t('PIPELINE.FLOW_BUILDER.NODE_EDITOR_REGION')"
            class="w-[380px] flex-shrink-0 flex flex-col overflow-hidden"
          >
            <NodeEditor
              :node-id="editingNodeId"
              @close="editingNodeId = null"
              @save="editingNodeId = null"
            />
          </div>
        </Transition>
      </template>
      <ExecutionHistory
        v-else-if="activeTab === 'history' && automationId"
        :automation-id="automationId"
        class="flex-1"
      />
      <div
        v-else-if="activeTab === 'history' && !automationId"
        class="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8"
      >
        <span class="i-lucide-save text-4xl text-n-slate-9" />
        <p class="text-sm text-n-slate-10">
          {{ t('PIPELINE.FLOW_BUILDER.SAVE_FIRST_MESSAGE') }}
        </p>
        <button
          class="px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors"
          @click="handleSaveClick"
        >
          {{ t('PIPELINE.FLOW_BUILDER.SAVE_NOW') }}
        </button>
      </div>
    </div>

    <!-- Modal para confirmar nome da automação -->
    <div
      v-if="showSaveModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="cancelModal"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl p-6 min-w-[400px] max-w-[90vw] outline outline-1 outline-n-weak"
      >
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.TITLE') }}
        </h3>
        <p class="text-label-small text-n-slate-11 mb-4">
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.DESCRIPTION') }}
        </p>
        <input
          v-model="modalName"
          type="text"
          class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent mb-3 text-sm"
          :placeholder="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')"
          @keyup.enter="confirmSave"
        />
        <textarea
          v-model="automationDescription"
          rows="2"
          class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent mb-4 text-sm resize-none"
          :placeholder="
            t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.DESCRIPTION_PLACEHOLDER') ||
            'Description (optional)'
          "
        />
        <div class="flex justify-end gap-3">
          <button
            class="px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium"
            @click="cancelModal"
          >
            {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 rounded-md bg-n-brand text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            :disabled="!modalName.trim()"
            @click="confirmSave"
          >
            {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.SAVE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmação de cancelamento (substitui window.confirm) -->
    <div
      v-if="showCancelConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      @click.self="dismissCancel"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl p-6 min-w-[400px] max-w-[90vw] outline outline-1 outline-n-weak"
      >
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.FLOW_BUILDER.CANCEL_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('PIPELINE.FLOW_BUILDER.CANCEL_CONFIRM') }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            class="px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-slate-6"
            @click="dismissCancel"
          >
            {{ t('PIPELINE.FLOW_BUILDER.CANCEL_KEEP_EDITING') }}
          </button>
          <button
            class="px-4 py-2 rounded-md bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
            @click="confirmCancel"
          >
            {{ t('PIPELINE.FLOW_BUILDER.CANCEL_DISCARD') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmação de execução REAL (dispara ações de verdade) -->
    <div
      v-if="showExecuteConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      @click.self="showExecuteConfirm = false"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl p-6 min-w-[400px] max-w-[90vw] outline outline-1 outline-n-weak"
      >
        <div class="flex items-center gap-2 mb-2">
          <span
            class="i-lucide-alert-triangle size-5 text-amber-9"
            aria-hidden="true"
          />
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_TITLE') }}
          </h3>
        </div>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_BODY') }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            class="px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-slate-6"
            @click="showExecuteConfirm = false"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 rounded-md bg-n-brand text-white hover:opacity-90 transition-opacity text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-brand"
            @click="runExecute"
          >
            {{ t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_EXECUTE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Painel de resultado: simulação (dry-run) OU execução real -->
    <div
      v-if="showRunModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      @click.self="closeRunModal"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col outline outline-1 outline-n-weak"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-5 py-3 border-b border-n-weak flex-shrink-0"
        >
          <div class="flex items-center gap-2">
            <span
              :class="
                runResult?.mode === 'dry_run'
                  ? 'i-lucide-flask-conical text-n-slate-11'
                  : runHasError
                    ? 'i-lucide-x-circle text-ruby-9'
                    : 'i-lucide-check-circle text-teal-9'
              "
              class="size-5"
              aria-hidden="true"
            />
            <h3 class="text-heading-3 text-n-slate-12">
              {{
                runResult?.mode === 'dry_run'
                  ? t('PIPELINE.FLOW_BUILDER.RUN.RESULT_TITLE_DRY')
                  : t('PIPELINE.FLOW_BUILDER.RUN.RESULT_TITLE_EXEC')
              }}
            </h3>
          </div>
          <button
            class="w-7 h-7 flex items-center justify-center rounded text-n-slate-10 hover:bg-n-alpha-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL')"
            @click="closeRunModal"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>

        <!-- Body -->
        <div class="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          <!-- Dry-run banner -->
          <p
            v-if="runResult?.mode === 'dry_run'"
            class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg px-3 py-2 flex items-center gap-2"
          >
            <span class="i-lucide-info size-3.5 shrink-0" aria-hidden="true" />
            {{ t('PIPELINE.FLOW_BUILDER.RUN.DRY_RUN_HINT') }}
          </p>

          <!-- Error block -->
          <div
            v-if="runHasError"
            class="rounded-lg bg-ruby-2 border border-ruby-5 p-3"
          >
            <p class="text-xs font-semibold text-ruby-11 mb-1">
              {{ t('PIPELINE.FLOW_BUILDER.RUN.ERROR_LABEL') }}
            </p>
            <p
              v-if="runFailedNodeLabel"
              class="text-xs font-medium text-ruby-11 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.RUN.FAILED_NODE', {
                  node: runFailedNodeLabel,
                })
              }}
            </p>
            <pre
              class="text-xs text-ruby-11 font-mono whitespace-pre-wrap break-all leading-relaxed"
              >{{
                runErrorText || t('PIPELINE.FLOW_BUILDER.RUN.UNKNOWN_ERROR')
              }}</pre
            >
          </div>

          <!-- Steps -->
          <ol v-if="runSteps.length" class="space-y-2">
            <li
              v-for="(step, idx) in runSteps"
              :key="step.node_id || idx"
              class="flex items-start gap-3 rounded-lg border border-n-weak p-2.5"
            >
              <span
                class="flex items-center justify-center size-5 rounded-full bg-n-alpha-2 text-[10px] font-semibold text-n-slate-11 shrink-0 mt-0.5"
              >
                {{ step.order ?? idx + 1 }}
              </span>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-n-slate-12 truncate">
                    {{ step.label || step.node_type || step.node_id }}
                  </span>
                  <span
                    v-if="step.node_type"
                    class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-11 shrink-0"
                  >
                    {{ step.node_type }}
                  </span>
                </div>
                <p
                  v-if="
                    Array.isArray(step.would_execute) &&
                    step.would_execute.length
                  "
                  class="text-[11px] text-n-slate-11 mt-0.5"
                >
                  {{ step.would_execute.join(' · ') }}
                </p>
              </div>
              <span
                v-if="step.result"
                class="text-[10px] px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                :class="
                  step.result === 'stop' || step.result === 'error'
                    ? 'bg-ruby-3 text-ruby-11'
                    : 'bg-teal-3 text-teal-11'
                "
              >
                {{ step.result }}
              </span>
            </li>
          </ol>

          <!-- Success without steps (execute happy path) -->
          <p
            v-else-if="!runHasError"
            class="text-sm text-n-slate-11 flex items-center gap-2"
          >
            <span
              class="i-lucide-check size-4 text-teal-9"
              aria-hidden="true"
            />
            {{ t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_SUCCESS') }}
          </p>
        </div>

        <!-- Footer -->
        <div
          class="px-5 py-3 border-t border-n-weak flex items-center justify-between flex-shrink-0"
        >
          <span class="text-xs text-n-slate-10">
            <template v-if="runSteps.length">
              {{
                t('PIPELINE.FLOW_BUILDER.RUN.STEPS_COUNT', {
                  count: runSteps.length,
                })
              }}
            </template>
          </span>
          <button
            class="px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="closeRunModal"
          >
            {{ t('PIPELINE.FLOW_BUILDER.RUN.CLOSE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
